import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { useParams } from "react-router-dom";
import {
  API_GET_USERS,
  API_GET_USER_LIMIT,
  API_UPDATE_USER_LIMIT,
} from "../../api/company.service";

export const Users = () => {
  const { companyId } = useParams();
  const [users, setUsers] = useState([]);
  const [limits, setLimits] = useState({
    supportCount: 0,
    adminCount: 0,
    campaignCount: 0,
    orderCount: 0,
  });
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (!companyId) return;

    const fetchUsers = async () => {
      const res = await API_GET_USERS(companyId);
      if (res?.user?.rows) {
        setUsers(res.user.rows);
      }
    };

    const fetchLimits = async () => {
      try {
        const res = await API_GET_USER_LIMIT(companyId);
        setLimits({
          supportCount: res?.supportCount ?? 0,
          adminCount: res?.adminCount ?? 0,
          campaignCount: res?.campaignCount ?? 0,
          orderCount: res?.orderCount ?? 0,
        });
      } catch (err) {
        console.error("Failed to fetch limits:", err);
      }
    };

    fetchUsers();
    fetchLimits();
  }, [companyId]);

  const handleLimitChange = (e, key) => {
    setLimits((prev) => ({ ...prev, [key]: e.value }));
  };

  const handleSave = async () => {
    try {
      await API_UPDATE_USER_LIMIT(companyId, limits);
      const updated = await API_GET_USER_LIMIT(companyId);
      setLimits({
        supportCount: updated?.supportCount ?? 0,
        adminCount: updated?.adminCount ?? 0,
        campaignCount: updated?.campaignCount ?? 0,
        orderCount: updated?.orderCount ?? 0,
      });
      setShowDialog(false);
    } catch (err) {
      console.error("Failed to update limits:", err);
    }
  };

  const generateDropdownOptions = (currentValues) => {
    const max = Math.max(200, ...Object.values(currentValues));
    return Array.from({ length: max + 1 }, (_, i) => ({
      label: `${i}`,
      value: i,
    }));
  };

  const [dropdownOptions, setDropdownOptions] = useState([]);

  useEffect(() => {
    if (!companyId) return;

    const fetchData = async () => {
      try {
        const usersRes = await API_GET_USERS(companyId);
        const limitsRes = await API_GET_USER_LIMIT(companyId);

        const latestLimits = {
          supportCount: limitsRes?.supportCount ?? 0,
          adminCount: limitsRes?.adminCount ?? 0,
          campaignCount: limitsRes?.campaignCount ?? 0,
          orderCount: limitsRes?.orderCount ?? 0,
        };

        setUsers(usersRes?.user?.rows || []);
        setLimits(latestLimits);
        setDropdownOptions(generateDropdownOptions(latestLimits));
      } catch (err) {
        console.error("Failed to fetch data:", err);
      }
    };

    fetchData();
  }, [companyId]);

  const convertDate = (user) => {
    const createdDate = new Date(user.createdAt);
    return <span>{createdDate.toLocaleString()}</span>;
  };

  return (
    <div style={{ padding: "2rem", background: "#f9f9f9" }}>
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "2rem",
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: "1.5rem", color: "#333" }}>
              Users
            </h2>
            <p style={{ margin: 0, fontSize: "0.95rem", color: "#666" }}>
              Manage users and their role limits
            </p>
          </div>
          <Button
            label="Config User Limits"
            icon="pi pi-user-plus"
            onClick={async () => {
              try {
                const res = await API_GET_USER_LIMIT(companyId);
                const latest = {
                  supportCount: res?.supportCount ?? 0,
                  adminCount: res?.adminCount ?? 0,
                  campaignCount: res?.campaignCount ?? 0,
                  orderCount: res?.orderCount ?? 0,
                };
                setLimits(latest);
                setDropdownOptions(generateDropdownOptions(latest));
                setShowDialog(true);
              } catch (err) {
                console.error(
                  "Failed to fetch limits before opening dialog:",
                  err
                );
              }
            }}
            className="p-button-rounded p-button-outlined"
          />
        </div>

        <DataTable
          value={users}
          paginator
          rows={5}
          rowsPerPageOptions={[5, 10, 25]}
          stripedRows
          responsiveLayout="scroll"
          style={{ borderRadius: "10px", overflow: "hidden" }}
        >
          <Column field="id" header="ID" style={{ minWidth: "4rem" }} />
          <Column field="userName" header="User Name" />
          <Column field="name" header="Name" />
          <Column field="role" header="Role" />
          <Column field="isActive" header="Active" />
          <Column header="Created At" body={convertDate} />
        </DataTable>
      </div>

      <Dialog
        header="Update User Limits"
        visible={showDialog}
        style={{ width: "35vw", borderRadius: "12px" }}
        onHide={() => setShowDialog(false)}
        className="p-fluid"
        footer={
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}
          >
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowDialog(false)}
              className="p-button-outlined"
            />
            <Button label="Save" icon="pi pi-check" onClick={handleSave} />
          </div>
        }
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
            paddingTop: "1rem",
          }}
        >
          {[
            { key: "adminCount", label: "Admin Count" },
            { key: "supportCount", label: "Support Count" },
            { key: "campaignCount", label: "Campaign Count" },
            { key: "orderCount", label: "Order Count" },
          ].map(({ key, label }) => (
            <div key={key} style={{ display: "flex", flexDirection: "column" }}>
              <label
                htmlFor={key}
                style={{
                  marginBottom: "0.5rem",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                {label}
              </label>
              <Dropdown
                value={limits[key]}
                options={dropdownOptions}
                onChange={(e) => handleLimitChange(e, key)}
                placeholder="Select"
                style={{ borderRadius: "8px" }}
              />
            </div>
          ))}
        </div>
      </Dialog>
    </div>
  );
};
